import React, { useEffect, useRef } from "react";
import { L } from "./helpers";
import { useCartCrossSell } from "./hooks/useCartCrossSell";
import { trackCrossSellEvent } from "./hooks/useCrossSellTracking";
import type { CartItem, Lang, Product, Translations } from "./types";

interface Props {
  cart: CartItem[];
  /** Full in-memory catalog used to look up product display info by dbId/id. */
  products: Product[];
  addCart: (p: Product, qty?: number, variantId?: string | null) => void;
  $: (n: number) => string;
  lang: Lang;
  t: Translations;
  limit?: number;
  /** Tracking source — "cart" (drawer) by default, "checkout" for review step. */
  source?: "cart" | "checkout";
  /** Optional heading override. */
  title?: string;
}

/**
 * Cross-sell strip rendered above the cart footer. Uses real co-purchase data
 * from `order_items` (via `useCartCrossSell`); silently renders nothing when
 * there isn't enough purchase data yet so the drawer stays clean.
 */
export function CartCrossSell({ cart, products, addCart, $, lang, t, limit = 4, source = "cart", title }: Props) {
  const cartDbIds = cart
    .map((it: any) => (typeof it.dbId === "string" ? it.dbId : null))
    .filter((x): x is string => !!x);

  const { rows, loading } = useCartCrossSell(cartDbIds, limit);
  const containerRef = useRef<HTMLDivElement>(null);

  if (cart.length === 0) return null;
  if (loading) return null;

  const recs = rows
    .map((r) => products.find((p: any) => p.dbId === r.product_id || String(p.id) === r.product_id))
    .filter((p): p is Product => !!p)
    .slice(0, limit);

  if (recs.length === 0) return null;

  // Track impressions once when the strip scrolls into view.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let fired = false;
    const obs = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        for (const e of entries) {
          if (e.isIntersecting) {
            fired = true;
            recs.forEach((p, idx) => {
              trackCrossSellEvent({
                event_type: "impression",
                source,
                product_id: cartDbIds[0] ?? null,
                recommended_product_id: p.dbId ?? p.id,
                position: idx,
              });
            });
            obs.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recs.length, cartDbIds[0]]);

  return (
    <div
      ref={containerRef}
      data-testid={source === "checkout" ? "checkout-cross-sell" : "cart-cross-sell"}
      style={{ 
        padding: "10px 14px 4px",
        borderTop: "1px solid var(--bd)",
        background: "var(--bg2, transparent)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 22, height: 22, borderRadius: 8,
            background: "var(--ac)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 12,
          }}
        >🤖</span>
        <span style={{ flex: 1 }}>
          {title || (lang === "es" ? "NutriCoach sugiere" : "NutriCoach suggests")}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingBottom: 6,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {recs.map((p) => (
          <div
            key={String(p.id)}
            data-testid={`cart-cross-sell-item-${p.id}`}
            style={{
              flex: "0 0 120px",
              border: "1px solid var(--bd)",
              borderRadius: "var(--r1)",
              padding: 6,
              background: "var(--bg1, #fff)",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div
              style={{
                aspectRatio: "1 / 1",
                background: "var(--bg2, #f5f5f5)",
                borderRadius: "var(--r1)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={p.img}
                alt=""
                loading="lazy"
                decoding="async"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.25, minHeight: 28 }}>
              {L(p.nm, lang)}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{$(p.pr)}</div>
            <button
              className="btn bo"
              type="button"
              style={{ fontSize: 11, padding: "4px 6px", marginTop: 2 }}
              onClick={() => {
                trackCrossSellEvent({
                  event_type: "click",
                  source,
                  product_id: cartDbIds[0] ?? null,
                  recommended_product_id: p.dbId ?? p.id,
                  position: recs.indexOf(p),
                });
                addCart(p, 1, null);
              }}
            >
              + {t.addToCart || (lang === "es" ? "Agregar" : "Add")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}